/**
 * /admin/drift — NC-522 Phase 1 FE
 *
 * Read-only Drift-View über drei Tabs:
 *   - Drift: orphan + missing RGs aus dem Backend-Detektor
 *   - Persistent: markierte Ressourcen (in P1 immer leer, Schreibpfad kommt mit P2)
 *   - History: gefilterter Audit-Log (drift-cleanup, drift-persistent-mark/-unmark, drift-scan)
 *
 * Schreib-Aktionen (Mark Persistent, Cleanup) folgen in P2/P3.
 * Spec: docs/superpowers/specs/2026-05-22-nc-522-azure-portal-sync-design.md §4.5 + §9.4
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { formatDateSafe } from '../utils/dateFormat';
import { auditApi, driftApi } from '../services/api';
import {
  AuditLogEntry,
  AzurePersistentResource,
  DriftEntry,
  DriftKind,
  DriftListingResponse,
  PersistentResourcesListingResponse,
} from '../services/types';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';

type TabKey = 'drift' | 'persistent' | 'history';

const DRIFT_AUDIT_ACTIONS = [
  'drift-scan',
  'drift-cleanup',
  'drift-persistent-mark',
  'drift-persistent-unmark',
];

function formatTimestamp(iso?: string): string {
  // formatDateSafe guards the parseISO(undefined) crash class and returns '—'
  // for missing/invalid input; preserve the prior raw-string fallback for
  // non-empty-but-unparseable values (NC-939).
  const formatted = formatDateSafe(iso, 'dd.MM.yyyy HH:mm');
  return formatted === '—' && iso ? iso : formatted;
}

function kindChipColor(kind: DriftKind): 'warning' | 'info' | 'success' {
  switch (kind) {
    case 'orphan':
      return 'warning';
    case 'missing':
      return 'info';
    case 'persistent':
      return 'success';
  }
}

function shortSub(sub: string): string {
  if (!sub) return '—';
  return sub.length > 12 ? `${sub.slice(0, 8)}…` : sub;
}

export default function DriftPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const [tab, setTab] = useState<TabKey>('drift');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [workloadFilter, setWorkloadFilter] = useState('');

  const {
    data: driftData,
    loading: driftLoading,
    error: driftError,
    refetch: refetchDrift,
  } = useApiQuery<DriftListingResponse>(
    () => driftApi.listDrift(),
    [isAdmin],
    { enabled: isAdmin }
  );

  const {
    data: persistentData,
    loading: persistentLoading,
    error: persistentError,
    refetch: refetchPersistent,
  } = useApiQuery<PersistentResourcesListingResponse>(
    () => driftApi.listPersistent(),
    [isAdmin],
    { enabled: isAdmin }
  );

  const {
    data: historyData,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useApiQuery<AuditLogEntry[]>(
    async () => {
      // The audit-log endpoint accepts a single `action` filter, so fan-out
      // four calls in parallel and merge — typical drift activity is in the
      // tens of rows per month, so this stays small.
      const lists = await Promise.all(
        DRIFT_AUDIT_ACTIONS.map((action) => auditApi.list({ action }))
      );
      const merged = lists.flat();
      merged.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      return merged;
    },
    [isAdmin],
    { enabled: isAdmin }
  );

  const driftEntries = useMemo(
    () => driftData?.resources ?? [],
    [driftData],
  );

  const subscriptionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of driftEntries) {
      if (e.subscriptionId) set.add(e.subscriptionId);
    }
    return Array.from(set).sort();
  }, [driftEntries]);

  const workloadOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of driftEntries) {
      if (e.workload) set.add(e.workload);
    }
    return Array.from(set).sort();
  }, [driftEntries]);

  const filteredDrift = useMemo(() => {
    return driftEntries.filter((e) => {
      if (subscriptionFilter && e.subscriptionId !== subscriptionFilter) return false;
      if (workloadFilter && e.workload !== workloadFilter) return false;
      return true;
    });
  }, [driftEntries, subscriptionFilter, workloadFilter]);

  const handleRefreshDriftAndPersistent = useCallback(() => {
    refetchDrift();
    refetchPersistent();
  }, [refetchDrift, refetchPersistent]);

  const hasActiveFilters = !!(subscriptionFilter || workloadFilter);

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Zugriff verweigert — diese Seite ist nur für Admins zugänglich.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Azure-Portal Drift-Check</Typography>
          <Typography variant="body2" color="text.secondary">
            Abgleich Azure-Ressourcengruppen ↔ Cosmos-Runs.
            {driftData?.lastScanUtc && (
              <> Letzter Scan: {formatTimestamp(driftData.lastScanUtc)}</>
            )}
          </Typography>
        </Box>
        <Tooltip title="Drift + Persistent neu laden">
          <span>
            <IconButton
              onClick={handleRefreshDriftAndPersistent}
              size="large"
              disabled={driftLoading || persistentLoading}
              aria-label="Drift aktualisieren"
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v as TabKey)}
        sx={{ mb: 2 }}
        aria-label="Drift-Sichten"
      >
        <Tab value="drift" label={`Drift (${driftEntries.length})`} />
        <Tab value="persistent" label={`Persistent (${persistentData?.count ?? 0})`} />
        <Tab value="history" label="History" />
      </Tabs>

      {tab === 'drift' && (
        <DriftTab
          loading={driftLoading}
          error={driftError}
          entries={filteredDrift}
          totalEntries={driftEntries.length}
          subscriptionFilter={subscriptionFilter}
          workloadFilter={workloadFilter}
          subscriptionOptions={subscriptionOptions}
          workloadOptions={workloadOptions}
          hasActiveFilters={hasActiveFilters}
          onSubscriptionChange={setSubscriptionFilter}
          onWorkloadChange={setWorkloadFilter}
          onRetry={refetchDrift}
          onClearFilters={() => {
            setSubscriptionFilter('');
            setWorkloadFilter('');
          }}
        />
      )}

      {tab === 'persistent' && (
        <PersistentTab
          loading={persistentLoading}
          error={persistentError}
          entries={persistentData?.entries ?? []}
          onRetry={refetchPersistent}
        />
      )}

      {tab === 'history' && (
        <HistoryTab
          loading={historyLoading}
          error={historyError}
          entries={historyData ?? []}
          onRetry={refetchHistory}
        />
      )}
    </Box>
  );
}

interface DriftTabProps {
  loading: boolean;
  error: string | null;
  entries: DriftEntry[];
  totalEntries: number;
  subscriptionFilter: string;
  workloadFilter: string;
  subscriptionOptions: string[];
  workloadOptions: string[];
  hasActiveFilters: boolean;
  onSubscriptionChange: (v: string) => void;
  onWorkloadChange: (v: string) => void;
  onRetry: () => void;
  onClearFilters: () => void;
}

function DriftTab(props: DriftTabProps) {
  const {
    loading,
    error,
    entries,
    totalEntries,
    subscriptionFilter,
    workloadFilter,
    subscriptionOptions,
    workloadOptions,
    hasActiveFilters,
    onSubscriptionChange,
    onWorkloadChange,
    onRetry,
    onClearFilters,
  } = props;

  return (
    <>
      {error && (
        <ErrorMessage
          message={error}
          title="Drift konnte nicht geladen werden"
          onRetry={onRetry}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Subscription"
                value={subscriptionFilter}
                onChange={(e) => onSubscriptionChange(e.target.value)}
                inputProps={{ 'aria-label': 'Subscription' }}
              >
                <MenuItem value="">Alle</MenuItem>
                {subscriptionOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {shortSub(s)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Workload"
                value={workloadFilter}
                onChange={(e) => onWorkloadChange(e.target.value)}
                inputProps={{ 'aria-label': 'Workload' }}
              >
                <MenuItem value="">Alle</MenuItem>
                {workloadOptions.map((w) => (
                  <MenuItem key={w} value={w}>
                    {w}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {hasActiveFilters && (
              <Grid item xs={12} sm={12} md={4}>
                <Button size="small" onClick={onClearFilters}>
                  Filter zurücksetzen
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        {loading && entries.length === 0 ? (
          <TableSkeleton
            rows={6}
            columns={6}
            headers={['Typ', 'Resource Group', 'Subscription', 'Workload', 'Run', 'Erstellt']}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            variant={hasActiveFilters ? 'no-results' : 'empty'}
            title={
              hasActiveFilters
                ? 'Keine Drift mit den aktuellen Filtern'
                : totalEntries === 0
                  ? 'Keine Drift erkannt'
                  : 'Keine passenden Einträge'
            }
            description={
              hasActiveFilters
                ? 'Filter zurücksetzen, um alle Drift-Einträge zu sehen.'
                : 'Azure und Cosmos sind synchron für die konfigurierten Workloads.'
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Typ</TableCell>
                  <TableCell>Resource Group</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Workload</TableCell>
                  <TableCell>Run</TableCell>
                  <TableCell>Erstellt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={`${e.kind}-${e.resourceGroup}-${e.subscriptionId}`} hover>
                    <TableCell>
                      <Chip label={e.kind} size="small" color={kindChipColor(e.kind)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {e.resourceGroup}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={e.subscriptionId || 'unbekannt'}>
                        <Typography variant="caption" color="text.secondary">
                          {shortSub(e.subscriptionId)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{e.workload ?? '—'}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {e.runId ?? e.cosmosRunId ?? '—'}
                        </Typography>
                        {e.cosmosStatus && (
                          <Typography variant="caption" color="text.secondary">
                            {e.cosmosStatus}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatTimestamp(e.azureCreatedAtUtc)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </>
  );
}

interface PersistentTabProps {
  loading: boolean;
  error: string | null;
  entries: AzurePersistentResource[];
  onRetry: () => void;
}

function PersistentTab({ loading, error, entries, onRetry }: PersistentTabProps) {
  return (
    <>
      {error && (
        <ErrorMessage
          message={error}
          title="Persistente Ressourcen konnten nicht geladen werden"
          onRetry={onRetry}
        />
      )}

      <Card>
        {loading && entries.length === 0 ? (
          <TableSkeleton
            rows={3}
            columns={4}
            headers={['Resource', 'Subscription', 'Markiert von', 'Markiert am']}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            variant="empty"
            title="Keine persistenten Ressourcen markiert"
            description="In Phase 1 ist der Schreibpfad noch nicht aktiv — Markierung folgt in Phase 2."
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Markiert von</TableCell>
                  <TableCell>Markiert am</TableCell>
                  <TableCell>Grund</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {e.resourceName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={e.subscriptionId}>
                        <Typography variant="caption" color="text.secondary">
                          {shortSub(e.subscriptionId)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{e.markedBy}</TableCell>
                    <TableCell>{formatTimestamp(e.markedAtUtc)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {e.reason || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </>
  );
}

interface HistoryTabProps {
  loading: boolean;
  error: string | null;
  entries: AuditLogEntry[];
  onRetry: () => void;
}

function HistoryTab({ loading, error, entries, onRetry }: HistoryTabProps) {
  return (
    <>
      {error && (
        <ErrorMessage
          message={error}
          title="Audit-History konnte nicht geladen werden"
          onRetry={onRetry}
        />
      )}

      <Card>
        {loading && entries.length === 0 ? (
          <TableSkeleton
            rows={6}
            columns={5}
            headers={['Zeitstempel', 'Aktion', 'Benutzer', 'Ergebnis', 'Fehler']}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            variant="empty"
            title="Keine Drift-Aktionen im Audit-Log"
            description="Cleanup-Aktionen und Persistent-Markierungen werden hier sichtbar, sobald sie ausgeführt werden."
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zeitstempel</TableCell>
                  <TableCell>Aktion</TableCell>
                  <TableCell>Benutzer</TableCell>
                  <TableCell>Ergebnis</TableCell>
                  <TableCell>Fehler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow key={`${entry.timestamp}-${entry.action}-${idx}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(entry.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.action}
                        size="small"
                        variant="outlined"
                        color={entry.action === 'drift-cleanup' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.userEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.result === 'success' ? 'Erfolg' : 'Fehler'}
                        size="small"
                        color={entry.result === 'success' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{
                          maxWidth: 280,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.errorMessage || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </>
  );
}
